<template>
  <CardLayout v-bind="clickable ? { onClick: () => $emit('click') } : {}">
    <template #header>
      <div class="space-y-3">
        <div class="flex items-center gap-2">
          <h3>{{ $t('report.sales_comparison_title') }}</h3>
          <button @click.stop="showComparisonInfo = true"
            class="flex items-center justify-center w-4 h-4 text-gray-400 hover:text-blue-500 transition-colors"
            :title="$t('report.info_button_title')">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none"
              viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"
              class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
            </svg>
          </button>
        </div>
        <div class="flex bg-muted rounded-lg p-1">
          <button @click.stop="comparisonMode = 'equitable'"
            :class="comparisonMode === 'equitable' ? 'bg-background shadow-sm' : ''"
            class="flex-1 py-2 px-3 text-sm rounded-md transition-all font-medium text-foreground">
            {{ $t('report.equitable') }}
          </button>
          <button @click.stop="comparisonMode = 'complete'"
            :class="comparisonMode === 'complete' ? 'bg-background shadow-sm' : ''"
            class="flex-1 py-2 px-3 text-sm rounded-md transition-all font-medium text-foreground">
            {{ $t('report.complete') }}
          </button>
        </div>
      </div>
    </template>
    <div v-if="salesLoaded" class="w-full grid grid-cols-2">
      <div class="flex flex-col">
        <span class="text-xl font-bold">
          {{ comparisonMode === 'equitable' ? current : currentComplete }}
        </span>
        <span class="text-sm text-gray-400 mt-1">
          {{ comparisonMode === 'equitable' ? previous : previousComplete }}
        </span>
      </div>
      <div v-if="card" class="self-center">
        <BarGauge
          :filling="comparisonMode === 'equitable' ? barFilling : barFillingComplete" />
      </div>
    </div>
    <div v-else>
      <Loading />
    </div>
  </CardLayout>

  <BottomSheet v-model:is-open="showComparisonInfo" variant="sidebar">
    <ComparisonInfoSheet
      :current-start-date="currentStartDate"
      :current-end-date="currentEndDate"
      :equitable-start-date="equitableStartDate"
      :equitable-end-date="equitableEndDate"
      :previous-start-date="previousStartDate"
      :previous-end-date="previousEndDate"
      :equitable-previous-start-date="equitablePreviousStartDate"
      :equitable-previous-end-date="equitablePreviousEndDate" />
  </BottomSheet>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { SalesReport } from '../../models/SalesReport'
import CardLayout from '../CardLayout.vue'
import BarGauge from '../charts/BarGauge.vue'
import Loading from '../Loading.vue'
import BottomSheet from '../BottomSheet.vue'
import ComparisonInfoSheet from '../ComparisonInfoSheet.vue'

const props = withDefaults(defineProps<{
  card: SalesReport | undefined
  clickable?: boolean
}>(), {
  clickable: true,
})

defineEmits<{
  click: []
}>()

const comparisonMode = ref<'equitable' | 'complete'>('equitable')
const showComparisonInfo = ref(false)

const salesLoaded = computed(() => {
  return props.card?.data != null
})

const current = computed(() => {
  return props.card?.getCurrentTotalFormatted()
})

const previous = computed(() => {
  return props.card?.getEquitableTotalFormatted()
})

const barFilling = computed(() => {
  const diff = props.card?.getDifferencePercentage() ?? 0

  if (isNaN(diff)) {
    return 0
  }

  if (diff > 0) {
    return 100
  }

  return 100 + diff * 100
})

const currentComplete = computed(() => {
  return props.card?.getCurrentTotalFormatted()
})

const previousComplete = computed(() => {
  return props.card?.getPreviousTotalFormatted()
})

const barFillingComplete = computed(() => {
  const currentTotal = props.card?.getCurrentTotal() ?? 0
  const previousTotal = props.card?.getPreviousTotal() ?? 0

  if (previousTotal === 0) {
    return currentTotal > 0 ? 100 : 0
  }

  const diff = ((currentTotal - previousTotal) / previousTotal) * 100

  if (isNaN(diff)) {
    return 0
  }

  if (diff > 0) {
    return 100
  }

  return Math.max(0, 100 + diff)
})

// Dates for ComparisonInfoSheet
const currentStartDate = computed(() => {
  return props.card?.data?.period.start ? new Date(props.card.data.period.start) : undefined
})

const currentEndDate = computed(() => {
  return new Date()
})

const equitableStartDate = computed(() => {
  return props.card?.data?.equitable_period?.start ? new Date(props.card.data.equitable_period.start) : undefined
})

const equitableEndDate = computed(() => {
  return props.card?.data?.equitable_period?.end ? new Date(props.card.data.equitable_period.end) : undefined
})

const previousStartDate = computed(() => {
  return props.card?.data?.previous_period?.start ? new Date(props.card.data.previous_period.start) : undefined
})

const previousEndDate = computed(() => {
  return props.card?.data?.previous_period?.end ? new Date(props.card.data.previous_period.end) : undefined
})

const equitablePreviousStartDate = computed(() => {
  return props.card?.data?.equitable_previous_period?.start ? new Date(props.card.data.equitable_previous_period.start) : undefined
})

const equitablePreviousEndDate = computed(() => {
  return props.card?.data?.equitable_previous_period?.end ? new Date(props.card.data.equitable_previous_period.end) : undefined
})
</script>
